import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { listHistory, type HistoryItem } from "@/lib/account.functions";
import { Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/history")({
  head: () => ({ meta: [{ title: "Reading History - Smartasaurus" }, { name: "robots", content: "noindex" }] }),
  component: HistoryPage,
});

function HistoryPage() {
  const fetch = useServerFn(listHistory);
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  useEffect(() => { fetch().then((r) => setItems(r.items)).catch(() => setItems([])); }, [fetch]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Activity</div>
        <h1 className="mt-3 font-display text-5xl uppercase leading-[0.95] md:text-7xl">Reading <span className="text-gradient-curiosity">history</span></h1>

        {items === null && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}
        {items?.length === 0 && (
          <div className="mt-10 rounded-2xl border border-border bg-card p-8 text-center">
            <Clock className="mx-auto h-6 w-6 text-primary" />
            <p className="mt-3 text-muted-foreground">Discoveries you read will show up here, with your progress.</p>
            <Link to="/discoveries" className="mt-5 inline-flex rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground">Start reading</Link>
          </div>
        )}
        {items && items.length > 0 && (
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
            {items.map((it) => (
              <Link key={it.discovery_id} to="/discovery/$slug" params={{ slug: it.slug }} className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40">
                {it.image_url && <img src={it.image_url} alt={it.title} loading="lazy" className="aspect-[5/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                <div className="min-w-0 p-3 md:p-4">
                  {it.category_label && <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{it.category_label}</div>}
                  <div className="mt-0.5 line-clamp-2 font-display text-sm uppercase leading-tight md:text-lg">{it.title}</div>
                  <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border">
                    <div className="h-full bg-gradient-curiosity" style={{ width: `${it.progress}%` }} />
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    {new Date(it.last_viewed_at).toLocaleDateString()} · {it.progress}% read
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
